import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { ArrowDown } from "../../../assets/images/ArrowDown";
import { Tick } from "../../../assets/images/Tick";
import { COLORS } from "../../../assets/constants/styles";
import { useTranslation } from "react-i18next";
import InputField from "../InputField/InputField";

export const Dropdown = ({
  label,
  options,
  defaultSelected,
  handleSelect,
  allowFiltering = false,
  placeholder = "",
  filterPlaceholder = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(options[defaultSelected]);
  const [dropUp, setDropUp] = useState(false);
  const [filterText, setFilterText] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelected(options[defaultSelected]);
  }, [options, defaultSelected]);

  // Filter options based on input text
  const filteredOptions = !filterText
    ? options
    : Object.entries(options).reduce((acc, [key, value]) => {
        if (String(value).toLowerCase().includes(filterText.toLowerCase())) {
          acc[key] = value;
        }
        return acc;
      }, {});

  const toggleDropdown = () => {
    if (!isOpen) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = Math.min(Object.keys(options).length * 40, 300); // Cap height
      setDropUp(spaceBelow < dropdownHeight);
    }
    setIsOpen((prev) => !prev);

    // Clear filter when dropdown closes
    if (isOpen) {
      setFilterText("");
    }
  };

  const onSelect = (key, value) => {
    handleSelect(key);
    setSelected(value);
    setIsOpen(false);
    setFilterText("");
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setFilterText("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <DropdownContainer ref={dropdownRef}>
      <Label>{label}</Label>
      <DropdownWrapper>
        <DropdownHeader onClick={toggleDropdown}>
          {selected || placeholder}
          <ArrowDown rotation={isOpen ? 180 : 0} />
        </DropdownHeader>
        <DropdownListContainer $dropUp={dropUp} $isOpen={isOpen}>
          {allowFiltering && (
            <FilterContainer onClick={(e) => e.stopPropagation()}>
              <InputField
                placeholder={filterPlaceholder || t("common.search")}
                value={filterText}
                onChange={setFilterText}
              />
            </FilterContainer>
          )}
          <DropdownList $maxHeight={allowFiltering}>
            {Object.keys(filteredOptions).length === 0 ? (
              <NoResultsItem>{t("common.noResultsFound")}</NoResultsItem>
            ) : (
              Object.entries(filteredOptions).map(([key, value]) => {
                const isSelected = value === selected;
                return (
                  <DropdownItem
                    key={key}
                    onClick={() => onSelect(key, value)}
                    $isSelected={isSelected}
                    $hasFilter={allowFiltering}
                  >
                    <Caption>{value}</Caption>
                    {isSelected && <Tick />}
                  </DropdownItem>
                );
              })
            )}
          </DropdownList>
        </DropdownListContainer>
      </DropdownWrapper>
    </DropdownContainer>
  );
};

const DropdownListContainer = styled.div`
  position: absolute;
  ${({ $dropUp }) =>
    $dropUp
      ? `
    bottom: calc(100% + 0.5rem);
  `
      : `
    top: calc(100% + 0.5rem);
  `}
  left: 0;
  width: 100%;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.lightGrey};
  border-radius: 0.5rem;
  padding: 0;
  margin: 0;
  list-style: none;
  z-index: 10;
  box-shadow: 0 0.25rem 0.375rem rgba(0, 0, 0, 0.1);

  filter: opacity(${({ $isOpen }) => ($isOpen ? 1 : 0)});
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateY(0)" : "translateY(-10px)"};
  transition: all 0.3s ease;
  pointer-events: ${({ $isOpen }) => ($isOpen ? "all" : "none")};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
`;

const DropdownList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: ${(props) => (props.$maxHeight ? "250px" : "300px")};
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
`;

const DropdownContainer = styled.div`
  font-family: "Manrope", sans-serif;
  width: 100%;
`;

const Label = styled.div`
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  color: ${COLORS.black};
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.lightGrey};
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  font-size: 1rem;
  color: ${COLORS.black};
`;

const FilterContainer = styled.div`
  padding: 0.625rem;
  border-bottom: 1px solid ${COLORS.lightGrey};

  /* Adjustments for InputField component */
  & > div {
    margin: 0;
  }

  /* Remove the InputField's default margins */
  & input {
    border-radius: 0.25rem;
  }
`;

const DropdownItem = styled.li`
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  font-size: 1rem;
  color: ${({ $isSelected }) => ($isSelected ? COLORS.violet : COLORS.grey)};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${COLORS.lightViolet};
  }

  &:first-child {
    border-top-left-radius: ${(props) => (props.$hasFilter ? "0" : "0.5rem")};
    border-top-right-radius: ${(props) => (props.$hasFilter ? "0" : "0.5rem")};
  }

  &:last-child {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
`;

const NoResultsItem = styled.li`
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  color: ${COLORS.lightGrey};
  text-align: center;
  font-style: italic;
`;

const Caption = styled.div``;
